import { ReadonlyVec2, vec2 } from 'gl-matrix';
import { ROTATION_CURSOR } from '~/engine/data/common/customs/cursors/rotationCursor';
import { localization } from '~/i18n';
import { errorName } from '~/utils/errors';
import { enqueueSnackbar } from '~/utils/notifications';
import {
    GizmoGeometry,
    TransformGizmo,
} from '~/engine/data/common/customs/TransformGizmo';
import { Environment } from '~/engine/data/Environment';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { LayerBit } from '~/engine/view/layers/LayerBit';
import type { EnvironmentViews } from '~/engine/view/EnvironmentViews';
import { TextureView } from '~/engine/view/TextureView';
import { PlaneView } from '~/engine/view/views/PlaneView';
import { StructureRotateAction } from '../../actions/transform-actions/StructureRotateAction';
import { StructureScaleAction } from '../../actions/transform-actions/StructureScaleAction';
import { StructureTranslateAction } from '../../actions/transform-actions/StructureTranslateAction';
import { StructureTransformAction } from '../../actions/transform-actions/transformUtils';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../../EventHandlerResult';
import { ModifierKeys } from '../../ModifierKeys';
import { MOUSE_BUTTON_LEFT, MOUSE_BUTTONS_NONE } from '../../MouseButton';
import { CrosshairInteractionMode, Tool } from '../../Tool';
import { writeRefusal } from './editableView';

type HitZone =
    | 'inside'
    | 'corner'
    | 'edge-x'
    | 'edge-y'
    | 'circle-boundary'
    | 'outside';

/** Pixel tolerance for clicking on the circle outline. */
const CIRCLE_HIT_TOLERANCE = 8;

/** Pixel tolerance for clicking on corner handles. */
const CORNER_HIT_TOLERANCE = 10;

/** Pixel tolerance for clicking on edge midpoint indicators. */
const EDGE_HIT_TOLERANCE = 10;

const NOT_HANDLED = new EventHandlerResult(
    EventHandlerResultType.EVENT_NOT_HANDLED
);

/**
 * Interactive tool for translating, scaling, and rotating a
 * selected structure.
 *
 * A bounding-box gizmo (rectangle + circumscribed circle +
 * corner handles + edge midpoint indicators) is drawn around
 * the structure in every view of the armed environment while
 * the tool is active: a view that joins after arming gets its
 * gizmo, a view that leaves gives it up.
 *
 * - **Corner handles** activate uniform 3D scaling.
 * - **Edge midpoints** activate single-axis scaling
 *   (left/right = view-X, top/bottom = view-Y).
 * - **Circle outline** activates rotation around the view normal.
 * - **Inside the box** activates translation.
 *
 * A left press on a view of another environment warns with the
 * reason ({@link refusalFor}); a press outside the gizmo on an own view does
 * nothing. A drag takes moves from the view it started on only; the release
 * commits the state the last of those moves left. A drag that fails, or whose
 * tool is replaced, ends with the structure back as pressed.
 */
export class TransformTool extends Tool {
    public readonly toolType = 'transform';

    private readonly _roi: RegionOfInterest;
    private readonly _views: EnvironmentViews | undefined;
    private readonly _environment: Environment | undefined;
    private readonly _siblingSupports: readonly RegionOfInterest[];

    private _activeAction?: StructureTransformAction;
    /** The view the drag in progress started on. */
    private _activeView?: PlaneView;
    private _cursor: string | undefined;

    /** Gizmos keyed by view — one per PlaneView. */
    private readonly _gizmos = new Map<PlaneView, TransformGizmo>();

    /** Stops following the armed environment's views; cleared once called. */
    private _unsubscribeViews: (() => void) | undefined;

    constructor(
        roi: RegionOfInterest,
        views: EnvironmentViews | undefined,
        environment: Environment | undefined,
        siblingSupports?: RegionOfInterest[]
    ) {
        super();
        this._roi = roi;
        this._views = views;
        this._environment = environment;
        this._siblingSupports = siblingSupports ?? [];
        this.syncGizmos();
        this._unsubscribeViews = views?.subscribe(() => this.syncGizmos());
    }

    /** The views of the armed environment, as they are now. */
    private currentViews(): readonly PlaneView[] {
        return this._views?.current ?? [];
    }

    public override isActionInProgress(): boolean {
        return this._activeAction !== undefined;
    }

    public override getCrosshairInteractionMode(
        _isDrawingActiveInOtherView: boolean
    ): CrosshairInteractionMode {
        return Tool.gizmoCrosshairMode(this.isStrokeInProgress());
    }

    public onActivate(): EventHandlerResult {
        return new EventHandlerResult(
            EventHandlerResultType.EVENT_HANDLED,
            false,
            { layers: LayerBit.CONTOUR_LAYER_BIT }
        );
    }

    public onDeactivate(): EventHandlerResult {
        this._cursor = undefined;
        this._activeAction?.cancel();
        this._activeAction = undefined;
        this._activeView = undefined;
        return new EventHandlerResult(
            EventHandlerResultType.EVENT_HANDLED,
            false,
            { layers: LayerBit.CONTOUR_LAYER_BIT }
        );
    }

    public dispose(): void {
        this._unsubscribeViews?.();
        this._unsubscribeViews = undefined;
        for (const [view, gizmo] of this._gizmos) {
            view.removeCustomObject(gizmo);
            // Re-composite so the just-removed gizmo disappears immediately.
            view.renderLayers();
        }
        this._gizmos.clear();
    }

    public onMousePress(
        button: number,
        modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2,
        view: TextureView
    ): EventHandlerResult {
        if (button !== 0 || !(view instanceof PlaneView)) {
            return NOT_HANDLED;
        }
        // This press only ends a drag whose release the tool never saw.
        if (this._activeAction) {
            return this.onMouseRelease(button, coordinates, view);
        }
        const refusal = this.refusalFor(view);
        if (refusal !== undefined) {
            enqueueSnackbar(refusal, { variant: 'warning' });
            return NOT_HANDLED;
        }

        // Guard: do not modify approved/reviewed structures
        if (this._roi.canModifyContours === false) {
            enqueueSnackbar(
                'Transform is not allowed for this ROI' +
                    ' as it has already been reviewed.',
                { variant: 'warning' }
            );
            return NOT_HANDLED;
        }

        const texture = this._roi.texture;
        if (!texture || texture.isEmpty) {
            return NOT_HANDLED;
        }

        const zone = this.hitTest(view, coordinates);

        if (zone === 'outside') {
            return NOT_HANDLED;
        }
        // Only a translate moves the supports with the structure, and only
        // those with content.
        const locked = this._siblingSupports.some(
            (s) => s.canModifyContours === false && !s.isEmpty
        );
        if (zone === 'inside' && locked) {
            enqueueSnackbar(localization.t('toolbar.transform.supportLocked'), {
                variant: 'warning',
            });
            return NOT_HANDLED;
        }

        this._activeAction = this.createAction(zone, view);
        this._activeView = view;

        return this.runGestureStep((action) =>
            action.onMousePress(button, modifierKeys, coordinates, view)
        );
    }

    public onMouseMove(
        coordinates: ReadonlyVec2,
        buttons: number,
        view: TextureView
    ): EventHandlerResult {
        if (this._activeAction) {
            // No button held: the release happened where the tool could not
            // see it, off every view. It commits as any release does.
            if (buttons === MOUSE_BUTTONS_NONE) {
                return this.onMouseRelease(
                    MOUSE_BUTTON_LEFT,
                    coordinates,
                    view
                );
            }
            // The gesture is measured in the plane of the view it started
            // on; a move over another view is outside what that view shows.
            if (view !== this._activeView) {
                return new EventHandlerResult(
                    EventHandlerResultType.EVENT_HANDLED
                );
            }
            return this.runGestureStep((action) =>
                action.onMouseMove(coordinates, buttons, view)
            );
        }

        if (view instanceof PlaneView) {
            this.updateCursor(view, coordinates);
        }

        return NOT_HANDLED;
    }

    public onMouseRelease(
        button: number,
        coordinates: ReadonlyVec2,
        view: TextureView
    ): EventHandlerResult {
        if (this._activeAction) {
            this._cursor = 'default';
            const result = this.runGestureStep((action) =>
                action.onMouseRelease(button, coordinates, view)
            );
            this._activeAction = undefined;
            this._activeView = undefined;
            return result;
        }

        return NOT_HANDLED;
    }

    /** A zoom, pan or scroll leaves a drag in progress as it is: no button is a release only on a real move. */
    public override onViewChanged(
        coordinates: ReadonlyVec2,
        view: TextureView
    ): void {
        if (!this._activeAction) {
            super.onViewChanged(coordinates, view);
        }
    }

    /**
     * Runs `step` of the drag in progress. A step that throws ends the drag
     * with the structure back as pressed and tells the user; the log gets the
     * error's name alone, as its message can name the structure.
     */
    private runGestureStep(
        step: (action: StructureTransformAction) => EventHandlerResult
    ): EventHandlerResult {
        const action = this._activeAction;
        try {
            return action ? step(action) : NOT_HANDLED;
        } catch (error) {
            action?.cancel();
            this._activeAction = undefined;
            this._activeView = undefined;
            console.error(`TransformTool: failed (${errorName(error)})`);
            enqueueSnackbar(localization.t('toolbar.transform.failed'), {
                variant: 'error',
            });
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_HANDLED,
                false,
                { layers: LayerBit.CONTOUR_LAYER_BIT }
            );
        }
    }

    /**
     * A view of another environment, or with none armed, gets the
     * refusal of {@link writeRefusal}. A turned view is accepted: the
     * gizmo acts in its plane.
     */
    public override refusalFor(view: PlaneView): string | undefined {
        return writeRefusal(view, this._environment);
    }

    // ── Action factory ────────────────────────────────────────────

    private createAction(zone: HitZone, view: PlaneView) {
        switch (zone) {
            case 'inside':
                return new StructureTranslateAction(
                    this._roi,
                    this._siblingSupports
                );
            case 'corner':
                return new StructureScaleAction(this._roi, { type: 'uniform' });
            case 'edge-x':
                return new StructureScaleAction(this._roi, {
                    type: 'single-axis',
                    axis: view.viewXDirection,
                    canvasAxis: 'x',
                });
            case 'edge-y':
                return new StructureScaleAction(this._roi, {
                    type: 'single-axis',
                    axis: view.viewYDirection,
                    canvasAxis: 'y',
                });
            case 'circle-boundary':
                return new StructureRotateAction(this._roi);
            default:
                throw new Error(`Unexpected hit zone: ${zone}`);
        }
    }

    // ── Gizmo management ──────────────────────────────────────────

    /**
     * Keeps one gizmo per view of the armed environment: a view that left
     * gives its gizmo up, a view that joined gets one. A view that left has
     * already released its GL resources, so it is not repainted; a view that
     * joined paints its first frame after it is registered, so the gizmo is
     * in that frame.
     */
    private syncGizmos(): void {
        const views = this.currentViews();
        for (const [view, gizmo] of this._gizmos) {
            if (!views.includes(view)) {
                view.removeCustomObject(gizmo);
                this._gizmos.delete(view);
            }
        }
        const texture = this._roi.texture;
        if (!texture || texture.isEmpty) {
            return;
        }
        for (const view of views) {
            if (!this._gizmos.has(view)) {
                const gizmo = new TransformGizmo(this._roi, () =>
                    this._activeAction?.footprint()
                );
                this._gizmos.set(view, gizmo);
                view.addCustomObject(gizmo);
            }
        }
    }

    // ── Hit testing ───────────────────────────────────────────────

    /**
     * Determine the gizmo zone under the mouse position.
     *
     * Priority order (most specific first):
     * 1. Corner handles  → uniform 3D scale
     * 2. Edge midpoints  → single-axis scale
     * 3. Circle boundary → rotation
     * 4. Inside box      → translation
     * 5. Outside         → nothing
     */
    private hitTest(view: PlaneView, coordinates: ReadonlyVec2): HitZone {
        const gizmo = this._gizmos.get(view);
        if (!gizmo) {
            return 'outside';
        }

        const geometry = gizmo.getGizmoGeometry(view);
        if (!geometry) {
            return 'outside';
        }

        const canvasCoords = view.clientToCanvas(coordinates);
        const mouseX = canvasCoords[0];
        const mouseY = canvasCoords[1];

        // 1. Corner handles (highest priority)
        if (this.isNearCorner(geometry, mouseX, mouseY)) {
            return 'corner';
        }

        // 2. Edge midpoints
        const edgeZone = this.isNearEdgeMidpoint(geometry, mouseX, mouseY);
        if (edgeZone) {
            return edgeZone;
        }

        // 3. Circle boundary
        const dist = vec2.distance(
            canvasCoords as vec2,
            geometry.center as vec2
        );
        if (Math.abs(dist - geometry.radius) < CIRCLE_HIT_TOLERANCE) {
            return 'circle-boundary';
        }

        // 4. Inside bounding box (rectangle, not circle)
        const cx = geometry.center[0];
        const cy = geometry.center[1];
        const hx = geometry.halfSize[0];
        const hy = geometry.halfSize[1];
        if (Math.abs(mouseX - cx) < hx && Math.abs(mouseY - cy) < hy) {
            return 'inside';
        }

        return 'outside';
    }

    private isNearCorner(
        geometry: GizmoGeometry,
        mouseX: number,
        mouseY: number
    ): boolean {
        for (const corner of geometry.corners) {
            const dx = mouseX - corner[0];
            const dy = mouseY - corner[1];
            if (
                dx * dx + dy * dy <
                CORNER_HIT_TOLERANCE * CORNER_HIT_TOLERANCE
            ) {
                return true;
            }
        }
        return false;
    }

    private isNearEdgeMidpoint(
        geometry: GizmoGeometry,
        mouseX: number,
        mouseY: number
    ): 'edge-x' | 'edge-y' | null {
        const mp = geometry.edgeMidpoints;
        const tolSq = EDGE_HIT_TOLERANCE * EDGE_HIT_TOLERANCE;

        // Left and right midpoints → scale in view-X direction
        for (const pt of [mp.left, mp.right]) {
            const dx = mouseX - pt[0];
            const dy = mouseY - pt[1];
            if (dx * dx + dy * dy < tolSq) {
                return 'edge-x';
            }
        }

        // Top and bottom midpoints → scale in view-Y direction
        for (const pt of [mp.top, mp.bottom]) {
            const dx = mouseX - pt[0];
            const dy = mouseY - pt[1];
            if (dx * dx + dy * dy < tolSq) {
                return 'edge-y';
            }
        }

        return null;
    }

    // ── Cursor ────────────────────────────────────────────────────

    private static readonly CURSOR_MAP: Record<HitZone, string> = {
        inside: 'move',
        corner: 'nwse-resize',
        'edge-x': 'ew-resize',
        'edge-y': 'ns-resize',
        'circle-boundary': ROTATION_CURSOR,
        outside: 'default',
    };

    private updateCursor(view: PlaneView, coordinates: ReadonlyVec2): void {
        const zone = this.hitTest(view, coordinates);
        this._cursor = TransformTool.CURSOR_MAP[zone];
    }

    /** Resize, move or rotate cursor for the gizmo zone under the pointer. */
    public override get cursor(): string | undefined {
        return this._cursor;
    }
}
